import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, formatDate, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type {
  CaseParty,
  CaseStatus,
  CourtCase,
  Dispatch,
  Filing,
  FilingServiceRow,
  FilingType,
  HearingPurpose,
  HearingSchedule,
  Judgment,
  JudgmentOutcome,
  JudgmentWithDispatches,
} from '../../lib/types';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';
import { useApiQuery } from '../../lib/useApi';

const CASE_STATUSES: CaseStatus[] = [
  'FILED',
  'SERVED',
  'ISSUE_JOINED',
  'HEARING_SCHEDULED',
  'JUDGMENT_RESERVED',
  'JUDGMENT_DELIVERED',
  'CLOSED',
  'DISCONTINUED',
];
const FILING_TYPES: FilingType[] = ['PLAINT', 'DEFENCE', 'MOTION', 'AFFIDAVIT', 'EXHIBIT'];
const HEARING_PURPOSES: HearingPurpose[] = ['MENTION', 'HEARING', 'TRIAL', 'JUDGMENT_DELIVERY'];
const JUDGMENT_OUTCOMES: JudgmentOutcome[] = ['GRANTED', 'DISMISSED', 'CONVICTED', 'ACQUITTED', 'SETTLED', 'DEFAULT_JUDGMENT'];

const REGISTRY_ROLES = ['COURT_CLERK', 'REGISTRAR_COURT'];
const HEARING_ROLES = ['COURT_CLERK', 'REGISTRAR_COURT', 'JUDGE', 'TRIBUNAL_MEMBER'];
const JUDGE_ROLES = ['JUDGE', 'TRIBUNAL_MEMBER'];

export function CaseDetail() {
  const { id } = useParams<{ id: string }>();
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const canRegistry = roles.some((r) => REGISTRY_ROLES.includes(r));
  const canHearing = roles.some((r) => HEARING_ROLES.includes(r));
  const canJudge = roles.some((r) => JUDGE_ROLES.includes(r));

  const caseQ = useApiQuery<CourtCase>(id ? `/api/v1/judiciary/cases/${id}` : null);
  const [statusTarget, setStatusTarget] = useState<CaseStatus | ''>('');
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const [showNewFiling, setShowNewFiling] = useState(false);
  const [showNewHearing, setShowNewHearing] = useState(false);
  const [showAdjourn, setShowAdjourn] = useState<HearingSchedule | null>(null);
  const [showServe, setShowServe] = useState<Filing | null>(null);
  const [showReject, setShowReject] = useState<Filing | null>(null);
  const [showJudgment, setShowJudgment] = useState(false);
  const [lastServed, setLastServed] = useState<FilingServiceRow[] | null>(null);
  const [lastDispatches, setLastDispatches] = useState<Dispatch[] | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  if (caseQ.loading) return <Spinner label="Loading case…" />;
  if (caseQ.error) return <ErrorNote message={caseQ.error} />;
  const c = caseQ.data;
  if (!c) return null;

  const changeStatus = async () => {
    if (!statusTarget) return;
    setStatusBusy(true);
    setStatusError(null);
    try {
      await api.post(`/api/v1/judiciary/cases/${c.id}/status`, { status: statusTarget });
      setStatusTarget('');
      caseQ.reload();
    } catch (e) {
      setStatusError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Status change failed');
    } finally {
      setStatusBusy(false);
    }
  };

  const filingAction = async (filing: Filing, action: 'accept' | 'reject' | 'serve', body?: unknown) => {
    setActionError(null);
    try {
      if (action === 'serve') {
        const res = await api.post<{ filingId: string; services: FilingServiceRow[] }>(`/api/v1/judiciary/filings/${filing.id}/serve`, body);
        setLastServed(res.services);
      } else {
        await api.post(`/api/v1/judiciary/filings/${filing.id}/${action}`, body);
      }
      caseQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : `${action} failed`);
    }
  };

  const hearingAction = async (hearing: HearingSchedule, action: 'hold' | 'vacate' | 'adjourn', body?: unknown) => {
    setActionError(null);
    try {
      await api.post(`/api/v1/judiciary/hearings/${hearing.id}/${action}`, body);
      caseQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : `${action} failed`);
    }
  };

  return (
    <div>
      <PageHeader
        title={c.caseNumber}
        subtitle={`${c.division.replaceAll('_', ' ')}${c.sealed ? ' · SEALED' : ''} — ${c.natureOfClaim}`}
        actions={
          <>
            <StatusBadge status={c.status} />
            <span className="text-xs text-slate-400">Filed {formatDate(c.filedAt)}</span>
          </>
        }
      />

      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      {canRegistry ? (
        <div className="card mb-6 flex flex-wrap items-center gap-2 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Change case status</span>
          <select className="field-input !w-auto" value={statusTarget} onChange={(e) => setStatusTarget(e.target.value as CaseStatus | '')}>
            <option value="">Select next status…</option>
            {CASE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <button className="btn-secondary" onClick={changeStatus} disabled={statusBusy || !statusTarget}>
            {statusBusy ? 'Updating…' : 'Apply'}
          </button>
          {statusError ? <span className="text-xs text-rose-600">{statusError}</span> : null}
        </div>
      ) : null}

      <div className="mb-6 card p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Parties</h2>
        {c.parties.length === 0 ? (
          <EmptyState title="No parties recorded" />
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {c.parties.map((p: CaseParty) => (
              <div key={p.id} className="rounded-lg border border-slate-200 p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{p.role.replaceAll('_', ' ')}</div>
                <div className="text-sm font-medium text-slate-900">{p.fullName}</div>
                {p.nin ? <div className="text-xs text-slate-500">NIN {p.nin}</div> : null}
                {p.attorneyNin ? <div className="text-xs text-slate-400">Attorney {p.attorneyNin}</div> : null}
                {p.isMinor ? <div className="mt-1 text-[10px] font-semibold text-amber-600">MINOR</div> : null}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mb-6 card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-700">Filings</h2>
          <button className="btn-primary" onClick={() => setShowNewFiling(true)}>
            + New filing
          </button>
        </div>
        {lastServed ? (
          <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
            Served {lastServed.length} recipient(s):{' '}
            {lastServed.map((s) => `${s.recipientNin} (${s.notifyStatus})`).join(', ')}
          </div>
        ) : null}
        {c.filings.length === 0 ? (
          <EmptyState title="No filings yet" />
        ) : (
          <ul className="space-y-2">
            {c.filings.map((f: Filing) => (
              <li key={f.id} className="rounded-lg border border-slate-200 p-3.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">{f.type}</span>
                    <StatusBadge status={f.status} />
                    <span className="text-xs text-slate-400">{f.filingNumber}</span>
                  </div>
                  <span className="text-xs text-slate-400">{formatDateTime(f.createdAt)}</span>
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  Filed by {f.filedByNin}
                  {f.onBehalfOfParty ? ` on behalf of ${f.onBehalfOfParty.fullName}` : ''}
                  {f.feeAmount ? ` · fee ${scr(f.feeAmount)}` : ''}
                </div>
                {f.docIds.length > 0 ? <div className="mt-0.5 text-xs text-slate-400">Docs: {f.docIds.join(', ')}</div> : null}
                {f.rejectedReason ? <div className="mt-1 text-xs text-rose-600">Rejected: {f.rejectedReason}</div> : null}
                {canRegistry ? (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {f.status === 'SUBMITTED' ? (
                      <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => filingAction(f, 'accept')}>
                        Accept
                      </button>
                    ) : null}
                    {(f.status === 'SUBMITTED' || f.status === 'FEE_PENDING') ? (
                      <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setShowReject(f)}>
                        Reject
                      </button>
                    ) : null}
                    {f.status === 'ACCEPTED' ? (
                      <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setShowServe(f)}>
                        Serve
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mb-6 card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-700">Hearings</h2>
          {canHearing ? (
            <button className="btn-primary" onClick={() => setShowNewHearing(true)}>
              + Schedule hearing
            </button>
          ) : null}
        </div>
        {c.hearings.length === 0 ? (
          <EmptyState title="No hearings scheduled" />
        ) : (
          <ul className="space-y-2">
            {c.hearings.map((h: HearingSchedule) => (
              <li key={h.id} className="rounded-lg border border-slate-200 p-3.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">{h.purpose.replaceAll('_', ' ')}</span>
                    <StatusBadge status={h.status} />
                  </div>
                  <span className="text-xs text-slate-400">{formatDateTime(h.scheduledAt)}</span>
                </div>
                <div className="mt-1 text-xs text-slate-500">Courtroom {h.courtroom}</div>
                {h.adjournedReason ? <div className="mt-1 text-xs text-amber-700">Adjourned: {h.adjournedReason}</div> : null}
                {canHearing && h.status === 'SCHEDULED' ? (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => hearingAction(h, 'hold')}>
                      Mark held
                    </button>
                    <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setShowAdjourn(h)}>
                      Adjourn
                    </button>
                    <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => hearingAction(h, 'vacate')}>
                      Vacate
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-700">Judgments</h2>
          {canJudge ? (
            <button className="btn-primary" onClick={() => setShowJudgment(true)}>
              + Deliver judgment
            </button>
          ) : null}
        </div>
        {lastDispatches ? (
          <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Order dispatches</div>
            <ul className="space-y-1">
              {lastDispatches.map((d) => (
                <li key={d.id} className="flex items-center justify-between text-xs">
                  <span>{d.target.replaceAll('_', ' ')}</span>
                  <span className="flex items-center gap-2">
                    <StatusBadge status={d.status} />
                    {d.failureReason ? <span className="text-rose-600">{d.failureReason}</span> : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {c.judgments.length === 0 ? (
          <EmptyState title="No judgment delivered yet" />
        ) : (
          <ul className="space-y-2">
            {c.judgments.map((j: Judgment) => (
              <li key={j.id} className="rounded-lg border border-slate-200 p-3.5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-900">{j.outcome.replaceAll('_', ' ')}</span>
                  <StatusBadge status={j.publicationStatus} />
                </div>
                <div className="mt-1 text-xs text-slate-500">Delivered {formatDateTime(j.deliveredAt)}</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showNewFiling ? (
        <NewFilingModal
          caseId={c.id}
          parties={c.parties}
          onClose={() => setShowNewFiling(false)}
          onDone={() => {
            setShowNewFiling(false);
            caseQ.reload();
          }}
        />
      ) : null}
      {showNewHearing ? (
        <NewHearingModal
          caseId={c.id}
          onClose={() => setShowNewHearing(false)}
          onDone={() => {
            setShowNewHearing(false);
            caseQ.reload();
          }}
        />
      ) : null}
      {showAdjourn ? (
        <AdjournModal
          hearing={showAdjourn}
          onClose={() => setShowAdjourn(null)}
          onDone={() => {
            setShowAdjourn(null);
            caseQ.reload();
          }}
        />
      ) : null}
      {showServe ? (
        <ServeModal
          filing={showServe}
          onClose={() => setShowServe(null)}
          onDone={(services) => {
            setShowServe(null);
            setLastServed(services);
            caseQ.reload();
          }}
        />
      ) : null}
      {showReject ? (
        <RejectModal
          filing={showReject}
          onClose={() => setShowReject(null)}
          onDone={() => {
            setShowReject(null);
            caseQ.reload();
          }}
        />
      ) : null}
      {showJudgment ? (
        <JudgmentModal
          caseId={c.id}
          onClose={() => setShowJudgment(false)}
          onDone={(dispatches) => {
            setShowJudgment(false);
            setLastDispatches(dispatches);
            caseQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function NewFilingModal({
  caseId,
  parties,
  onClose,
  onDone,
}: {
  caseId: string;
  parties: CaseParty[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [type, setType] = useState<FilingType>('PLAINT');
  const [onBehalfOfPartyId, setOnBehalfOfPartyId] = useState('');
  const [docIds, setDocIds] = useState('');
  const [t3SignatureHash, setT3SignatureHash] = useState('');
  const [clientRef, setClientRef] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/judiciary/cases/${caseId}/filings`, {
        type,
        onBehalfOfPartyId: onBehalfOfPartyId || undefined,
        docIds: docIds
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        t3SignatureHash: type === 'AFFIDAVIT' ? t3SignatureHash || undefined : undefined,
        clientRef: clientRef || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to submit filing');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New filing" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Type">
          <select className="field-input" value={type} onChange={(e) => setType(e.target.value as FilingType)}>
            {FILING_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>
        <Field label="On behalf of party (optional)">
          <select className="field-input" value={onBehalfOfPartyId} onChange={(e) => setOnBehalfOfPartyId(e.target.value)}>
            <option value="">Not specified</option>
            {parties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.fullName} ({p.role.replaceAll('_', ' ')})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Document ids (comma separated, optional)">
          <input className="field-input" value={docIds} onChange={(e) => setDocIds(e.target.value)} placeholder="e.g. doc_a1, doc_a2" />
        </Field>
        {type === 'AFFIDAVIT' ? (
          <Field label="T3 signature hash (required — identity tier T3 signer)">
            <input className="field-input" value={t3SignatureHash} onChange={(e) => setT3SignatureHash(e.target.value)} placeholder="sha256 of the signed statement" />
          </Field>
        ) : null}
        <Field label="Client reference (optional)">
          <input className="field-input" value={clientRef} onChange={(e) => setClientRef(e.target.value)} placeholder="idempotency key for retries" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || (type === 'AFFIDAVIT' && t3SignatureHash.trim().length < 8)}
          >
            {busy ? 'Submitting…' : 'Submit filing'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function NewHearingModal({ caseId, onClose, onDone }: { caseId: string; onClose: () => void; onDone: () => void }) {
  const [courtroom, setCourtroom] = useState('');
  const [purpose, setPurpose] = useState<HearingPurpose>('MENTION');
  const [scheduledAt, setScheduledAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/judiciary/cases/${caseId}/hearings`, {
        courtroom,
        purpose,
        scheduledAt: new Date(scheduledAt).toISOString(),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to schedule hearing');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Schedule hearing" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Courtroom">
          <input className="field-input" value={courtroom} onChange={(e) => setCourtroom(e.target.value)} placeholder="e.g. Court Room 4" />
        </Field>
        <Field label="Purpose">
          <select className="field-input" value={purpose} onChange={(e) => setPurpose(e.target.value as HearingPurpose)}>
            {HEARING_PURPOSES.map((p) => (
              <option key={p} value={p}>
                {p.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Date &amp; time">
          <input type="datetime-local" className="field-input" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !courtroom.trim() || !scheduledAt}>
            {busy ? 'Scheduling…' : 'Schedule'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function AdjournModal({ hearing, onClose, onDone }: { hearing: HearingSchedule; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [newScheduledAt, setNewScheduledAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/judiciary/hearings/${hearing.id}/adjourn`, {
        reason,
        newScheduledAt: new Date(newScheduledAt).toISOString(),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Adjournment failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Adjourn hearing — ${hearing.courtroom}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Reason">
          <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Counsel unavailable" />
        </Field>
        <Field label="New date &amp; time">
          <input type="datetime-local" className="field-input" value={newScheduledAt} onChange={(e) => setNewScheduledAt(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || reason.trim().length < 3 || !newScheduledAt}>
            {busy ? 'Adjourning…' : 'Adjourn'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ServeModal({ filing, onClose, onDone }: { filing: Filing; onClose: () => void; onDone: (services: FilingServiceRow[]) => void }) {
  const [recipients, setRecipients] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ filingId: string; services: FilingServiceRow[] }>(`/api/v1/judiciary/filings/${filing.id}/serve`, {
        recipients: recipients
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      });
      onDone(res.services);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Service failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Serve filing — ${filing.filingNumber}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">Statutory e-service with delivery proof (SeyNotify).</p>
        <Field label="Recipient NINs (comma separated)">
          <input className="field-input" value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="e.g. SC870512033, SC910304018" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !recipients.trim()}>
            {busy ? 'Serving…' : 'Serve'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RejectModal({ filing, onClose, onDone }: { filing: Filing; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/judiciary/filings/${filing.id}/reject`, { reason });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Rejection failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Reject filing — ${filing.filingNumber}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Reason">
          <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Missing required exhibit" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-danger" onClick={submit} disabled={busy || reason.trim().length < 3}>
            {busy ? 'Rejecting…' : 'Reject'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

interface FineRow {
  nin: string;
  amountScr: string;
  description: string;
}
interface ConvictionRow {
  nin: string;
  offenceSummary: string;
}
interface LicenceRow {
  nin: string;
  action: 'SUSPEND' | 'REVOKE' | 'ENDORSE' | 'DISQUALIFY';
  licenceRef: string;
  target: 'M21' | 'M14';
}
interface CustodyRow {
  nin: string;
  termMonths: string;
  facility: string;
}

function JudgmentModal({ caseId, onClose, onDone }: { caseId: string; onClose: () => void; onDone: (dispatches: Dispatch[]) => void }) {
  const [outcome, setOutcome] = useState<JudgmentOutcome>('GRANTED');
  const [fines, setFines] = useState<FineRow[]>([]);
  const [convictions, setConvictions] = useState<ConvictionRow[]>([]);
  const [licenceActions, setLicenceActions] = useState<LicenceRow[]>([]);
  const [custodyOrders, setCustodyOrders] = useState<CustodyRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<JudgmentWithDispatches>(`/api/v1/judiciary/cases/${caseId}/judgments`, {
        outcome,
        orders: {
          fines: fines
            .filter((f) => f.nin && f.amountScr)
            .map((f) => ({ nin: f.nin, amountScr: Number.parseFloat(f.amountScr), description: f.description || undefined })),
          convictions: convictions.filter((c) => c.nin && c.offenceSummary),
          licenceActions: licenceActions
            .filter((l) => l.nin)
            .map((l) => ({ nin: l.nin, action: l.action, licenceRef: l.licenceRef || undefined, target: l.target })),
          custodyOrders: custodyOrders
            .filter((c) => c.nin)
            .map((c) => ({
              nin: c.nin,
              termMonths: c.termMonths ? Number.parseInt(c.termMonths, 10) : undefined,
              facility: c.facility || undefined,
            })),
        },
      });
      onDone(res.dispatches);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to deliver judgment');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Deliver judgment" onClose={onClose} wide>
      <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Outcome">
          <select className="field-input" value={outcome} onChange={(e) => setOutcome(e.target.value as JudgmentOutcome)}>
            {JUDGMENT_OUTCOMES.map((o) => (
              <option key={o} value={o}>
                {o.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>

        <OrderSection title="Fines (dispatched to SeyPay)" onAdd={() => setFines((r) => [...r, { nin: '', amountScr: '', description: '' }])}>
          {fines.map((f, i) => (
            <div key={i} className="mb-1.5 flex gap-1.5">
              <input className="field-input" placeholder="NIN" value={f.nin} onChange={(e) => setFines((r) => r.map((x, idx) => (idx === i ? { ...x, nin: e.target.value } : x)))} />
              <input className="field-input !w-28" placeholder="SCR" type="number" value={f.amountScr} onChange={(e) => setFines((r) => r.map((x, idx) => (idx === i ? { ...x, amountScr: e.target.value } : x)))} />
              <input className="field-input" placeholder="Description (optional)" value={f.description} onChange={(e) => setFines((r) => r.map((x, idx) => (idx === i ? { ...x, description: e.target.value } : x)))} />
              <button className="shrink-0 text-slate-400 hover:text-rose-600" onClick={() => setFines((r) => r.filter((_, idx) => idx !== i))}>
                ✕
              </button>
            </div>
          ))}
        </OrderSection>

        <OrderSection title="Convictions (dispatched to M05 conviction index)" onAdd={() => setConvictions((r) => [...r, { nin: '', offenceSummary: '' }])}>
          {convictions.map((c, i) => (
            <div key={i} className="mb-1.5 flex gap-1.5">
              <input className="field-input !w-40" placeholder="NIN" value={c.nin} onChange={(e) => setConvictions((r) => r.map((x, idx) => (idx === i ? { ...x, nin: e.target.value } : x)))} />
              <input className="field-input" placeholder="Offence summary" value={c.offenceSummary} onChange={(e) => setConvictions((r) => r.map((x, idx) => (idx === i ? { ...x, offenceSummary: e.target.value } : x)))} />
              <button className="shrink-0 text-slate-400 hover:text-rose-600" onClick={() => setConvictions((r) => r.filter((_, idx) => idx !== i))}>
                ✕
              </button>
            </div>
          ))}
        </OrderSection>

        <OrderSection
          title="Licence actions (M21 land transport dispatches for real; M14 recorded PENDING_DISPATCH)"
          onAdd={() => setLicenceActions((r) => [...r, { nin: '', action: 'SUSPEND', licenceRef: '', target: 'M21' }])}
        >
          {licenceActions.map((l, i) => (
            <div key={i} className="mb-1.5 flex gap-1.5">
              <input className="field-input !w-32" placeholder="NIN" value={l.nin} onChange={(e) => setLicenceActions((r) => r.map((x, idx) => (idx === i ? { ...x, nin: e.target.value } : x)))} />
              <select className="field-input !w-32" value={l.action} onChange={(e) => setLicenceActions((r) => r.map((x, idx) => (idx === i ? { ...x, action: e.target.value as LicenceRow['action'] } : x)))}>
                <option value="SUSPEND">Suspend</option>
                <option value="REVOKE">Revoke</option>
                <option value="ENDORSE">Endorse</option>
                <option value="DISQUALIFY">Disqualify</option>
              </select>
              <select className="field-input !w-24" value={l.target} onChange={(e) => setLicenceActions((r) => r.map((x, idx) => (idx === i ? { ...x, target: e.target.value as LicenceRow['target'] } : x)))}>
                <option value="M21">M21</option>
                <option value="M14">M14</option>
              </select>
              <input className="field-input" placeholder="Licence ref (optional)" value={l.licenceRef} onChange={(e) => setLicenceActions((r) => r.map((x, idx) => (idx === i ? { ...x, licenceRef: e.target.value } : x)))} />
              <button className="shrink-0 text-slate-400 hover:text-rose-600" onClick={() => setLicenceActions((r) => r.filter((_, idx) => idx !== i))}>
                ✕
              </button>
            </div>
          ))}
        </OrderSection>

        <OrderSection title="Custody orders (recorded PENDING_DISPATCH — M07 unbuilt)" onAdd={() => setCustodyOrders((r) => [...r, { nin: '', termMonths: '', facility: '' }])}>
          {custodyOrders.map((c, i) => (
            <div key={i} className="mb-1.5 flex gap-1.5">
              <input className="field-input !w-32" placeholder="NIN" value={c.nin} onChange={(e) => setCustodyOrders((r) => r.map((x, idx) => (idx === i ? { ...x, nin: e.target.value } : x)))} />
              <input className="field-input !w-24" placeholder="Months" type="number" value={c.termMonths} onChange={(e) => setCustodyOrders((r) => r.map((x, idx) => (idx === i ? { ...x, termMonths: e.target.value } : x)))} />
              <input className="field-input" placeholder="Facility (optional)" value={c.facility} onChange={(e) => setCustodyOrders((r) => r.map((x, idx) => (idx === i ? { ...x, facility: e.target.value } : x)))} />
              <button className="shrink-0 text-slate-400 hover:text-rose-600" onClick={() => setCustodyOrders((r) => r.filter((_, idx) => idx !== i))}>
                ✕
              </button>
            </div>
          ))}
        </OrderSection>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Delivering…' : 'Deliver judgment'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function OrderSection({ title, onAdd, children }: { title: string; onAdd: () => void; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-600">{title}</span>
        <button className="text-xs font-semibold text-brand-700 hover:underline" onClick={onAdd}>
          + Add
        </button>
      </div>
      {children}
    </div>
  );
}
